import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Card,
  CardActionArea,
  cardActionAreaClasses,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  ExpandMore,
  Typography,
} from "common/components";

import { formatCurrency } from "common/utils";
import { CardsSelectionType, CardsSize } from "core/actions/flows/steps/Cards";
import { LoadingView } from "core/components";
import { TFunction, useTranslation } from "i18n";
import React, { ReactElement, Suspense, useState } from "react";
import { FontIcon } from "../../assets";

interface CardOptionProps {
  /** Data to be displayed in the card */
  cardOptionData: CardOptionData;
  /** The index of the current option. */
  index: number;
  /** Indicates if the option is currently selected. */
  selected: boolean;
  /** Function to set the currency value. */
  setCurrency?: (value: string) => void;
  /** Function to set the amount value. */
  setAmount?: (value: number) => void;
  /** Function to set the selected indices. */
  setSelectedIndices: (value: React.SetStateAction<number[]>) => void;
  /** Size of cards */
  cardSize: CardsSize;
  /** If single or multi select */
  selectionType: CardsSelectionType;
}

/**
 * Card used to display card options.
 */
export function CardOption({
  selected,
  index,
  setCurrency, // will be added in next version
  setAmount, // will be added in next version
  setSelectedIndices,
  selectionType,
  cardSize,
  cardOptionData,
}: CardOptionProps): ReactElement {
  const { t } = useTranslation(["common"]);

  const [expanded, setExpanded] = useState(false);
  /**
   * Toggles the expanded state.
   * If the component is expanded, it collapses it, and if it is collapsed, it expands it.
   */
  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  /**
   * Handles the click event on the specified element.
   * If the clicked element is not an excluded node and no text is selected, it calls the handleSelection function.
   *
   * @param event - The mouse event triggered by clicking on an element.
   */
  const handleOnClick = (event: React.MouseEvent<HTMLElement>) => {
    const isClickEvent = event.type === "click";
    if (isClickEvent) {
      const excludedNodes = ["BUTTON", "svg"];
      const clickedNodeName = (event.target as HTMLElement).nodeName;
      const selection = window.getSelection()?.toString() ?? "";

      // Check if the clicked element is not one of the excluded nodes, and no selection of text
      if (!excludedNodes.includes(clickedNodeName) && selection.length === 0) {
        handleSelection();
      }
    }
  };

  /**
   * Handles key press events.
   * Calls the handleSelection function if space key is pressed.
   */
  const handleKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
    const isSpacebarPressed = event.key === " ";
    if (isSpacebarPressed) {
      handleSelection();
    }
  };

  /**
   * Handles the selection logic for single or multi-selection.
   */
  const handleSelection = () => {
    // Define the logic to update selected indices
    const updateSelectedIndices = (prevSelected: number[]) => {
      if (selectionType === "SINGLE") {
        // In SINGLE selection mode, only one index is selected
        return selected ? prevSelected.filter((i) => i !== index) : [index];
      }

      // In MULTI selection mode, add/remove the index to/from the selected indices
      return selected
        ? prevSelected.filter((i) => i !== index)
        : [...prevSelected, index];
    };

    setSelectedIndices(updateSelectedIndices);
  };

  return (
    <Card
      data-cy="optionsCard"
      sx={{
        width: cardSize === "SMALL" ? 250 : 300,
        margin: "3px",
        borderRadius: "1rem",
        position: "relative",
        ...(selected && {
          outline: "solid 3px",
          outlineColor: (theme) => theme.palette.primary.main,
        }),
      }}
      variant="outlined"
      onClick={handleOnClick}
    >
      {selected && (
        <Box
          sx={{
            position: "absolute",
            top: "8px",
            right: "10px",
          }}
        >
          <FontIcon icon={"check"} fontSize="medium" />
        </Box>
      )}

      <Suspense fallback={<LoadingView />}>
        <Box sx={{ height: cardSize === "SMALL" ? 30 : 70 }}>
          <CardHeader
            title={cardOptionData.title}
            data-cy="cardTitle"
            subheader={
              <Box
                sx={{
                  maxHeight: 80,
                  overflowY: "auto",
                }}
              >
                {cardOptionData?.subHeader?.map((item, index: number) => {
                  return (
                    <Chip
                      key={index}
                      label={item}
                      size="small"
                      sx={{
                        margin: "2px",
                        marginLeft: "1px",
                        marginRight: "0px",
                        background: (theme) => theme.palette.primary.light,
                      }}
                    />
                  );
                })}
              </Box>
            }
          />
        </Box>
        <CardActionArea
          onKeyDown={handleKeyPress}
          disableRipple
          sx={{
            "&:hover": {
              [`& .${cardActionAreaClasses.focusHighlight}`]: {
                opacity: 0,
              },
            },
          }}
        >
          <CardContent>
            <Typography
              variant="h4"
              sx={{ marginTop: "1.5em", marginBottom: "0.9em" }}
              color="text.secondary"
              align="center"
            >
              <b>{cardOptionData.price}</b>
            </Typography>
          </CardContent>
        </CardActionArea>
        <CardActions>
          <ExpandMore
            data-cy="expandButton"
            expand={expanded}
            onClick={handleExpandClick}
          >
            <ExpandMoreIcon />
          </ExpandMore>
        </CardActions>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent>
            <Typography component="p" data-cy="description">
              {t("common:description")}
            </Typography>
            <Typography component="p" variant="body2">
              {cardOptionData.description}
            </Typography>
          </CardContent>
        </Collapse>
      </Suspense>
    </Card>
  );
}

export type CardOptionData = {
  title: string;
  subHeader: string[];
  price: string;
  description: string;
};

/**
 * Transforms an option object to CardOptionData, including price formatting and translation.
 *
 * @param option - The option object containing card details.
 * @param t - The translation function from i18next.
 * @returns A transformed CardOption
 */
export function transformToCardOptionData(
  option: any,
  t: TFunction
): CardOptionData {
  switch (option.cardType) {
    case "ProductOfferingTopup":
      return {
        title: option.name ?? "",
        subHeader: [option.code ?? ""],
        price: option.priceInclTax
          ? `${formatCurrency(
              Number(option.priceInclTax),
              option.currencyCode
            )} (${t("common:cards.inclTax")})`
          : "",
        description: option.description ?? "",
      };

    default:
      return {
        title: "",
        subHeader: [""],
        price: "",
        description: "",
      };
  }
}
